import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { Amplify } from 'aws-amplify'
import { signIn, signOut, getCurrentUser, fetchAuthSession, confirmSignIn, SignInInput } from 'aws-amplify/auth'

// Configure Amplify for Platform Admin pool
const platformAdminConfig = {
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_PLATFORM_ADMIN_USER_POOL_ID || 'us-east-1_XXXXXXXXX',
      userPoolClientId: import.meta.env.VITE_PLATFORM_ADMIN_CLIENT_ID || 'XXXXXXXXXXXXXXXXXXXXXXXXXX',
      region: import.meta.env.VITE_AWS_REGION || 'us-east-1',
    }
  }
}

interface User {
  username: string
  email?: string
  attributes?: Record<string, any>
}

interface PlatformAdminAuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  completeNewPassword: (newPassword: string) => Promise<void>
  logout: () => Promise<void>
  isAuthenticated: boolean
  getToken: () => Promise<string | null>
}

const PlatformAdminAuthContext = createContext<PlatformAdminAuthContextType | undefined>(undefined)

export function PlatformAdminAuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [authReady, setAuthReady] = useState(false)

  useEffect(() => {
    // Configure Amplify for Platform Admin on mount
    Amplify.configure(platformAdminConfig)
    checkUser()
  }, [])

  async function checkUser() {
    try {
      Amplify.configure(platformAdminConfig)

      const currentUser = await getCurrentUser()

      const userData = {
        username: currentUser.username,
        attributes: currentUser.signInDetails?.loginId ? { email: currentUser.signInDetails.loginId } : {},
      }

      setUser(prevUser => {
        if (prevUser?.username === userData.username) {
          return prevUser;
        }
        return userData;
      });
      setAuthReady(true)
    } catch {
      setUser(null)
      setAuthReady(true)
    } finally {
      setLoading(false)
    }
  }

  async function login(email: string, password: string) {
    try {
      Amplify.configure(platformAdminConfig)

      // Sign out any existing user first
      try {
        await signOut()
      } catch {
        // No existing user to sign out
      }

      const signInInput: SignInInput = {
        username: email,
        password: password
      }
      const result = await signIn(signInInput)

      if (result.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        throw new Error('NEW_PASSWORD_REQUIRED')
      }

      const currentUser = await getCurrentUser()

      const userData = {
        username: currentUser.username,
        attributes: currentUser.signInDetails?.loginId ? { email: currentUser.signInDetails.loginId } : {},
      }

      setUser(userData)
      setAuthReady(true)
    } catch (error: any) {
      throw error
    }
  }

  async function completeNewPassword(newPassword: string) {
    try {
      await confirmSignIn({ challengeResponse: newPassword })
      await checkUser()
    } catch (error: any) {
      throw new Error(error.message || 'Failed to set new password')
    }
  }

  async function logout() {
    try {
      await signOut()
      setUser(null)
    } catch {
      // Logout failed silently
    }
  }

  async function getToken(): Promise<string | null> {
    try {
      const session = await fetchAuthSession()
      return session.tokens?.idToken?.toString() || null
    } catch {
      return null
    }
  }

  const value = {
    user,
    loading,
    login,
    completeNewPassword,
    logout,
    isAuthenticated: !!user,
    getToken,
  }

  return <PlatformAdminAuthContext.Provider value={value}>{children}</PlatformAdminAuthContext.Provider>
}

export function usePlatformAdminAuth() {
  const context = useContext(PlatformAdminAuthContext)
  if (context === undefined) {
    throw new Error('usePlatformAdminAuth must be used within a PlatformAdminAuthProvider')
  }
  return context
}
